import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { NearestStoreMatch } from '../../pages/groceries/display';
import { nearestStoreSuggestion, type GroceryListStatus } from '../../pages/groceries/display';
import { groceryKeys } from '../../pages/groceries/queries';
import { api } from '../../lib/api';
import { saveLastStoreId } from '../../lib/lastStore';
import { Button } from '../Button';

/**
 * PLAN §4.7 web preview: an optional "Detect store" affordance for the
 * shopping view. Geolocation is only ever read from a user gesture (the
 * button) or when the browser reports the permission is already granted —
 * this never triggers the browser's own permission prompt on its own.
 */
export function StoreDetectionBanner({
  listId,
  status,
  currentStoreId,
}: {
  listId: string;
  status: GroceryListStatus;
  currentStoreId: string | null;
}) {
  const qc = useQueryClient();
  const [suggestion, setSuggestion] = useState<NearestStoreMatch | null>(null);
  const [dismissedStoreId, setDismissedStoreId] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const autoAttempted = useRef(false);

  const locate = () =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('geolocation unsupported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10_000,
      });
    });

  const detect = useMutation({
    mutationFn: async () => {
      const pos = await locate();
      return api.stores.nearest(pos.coords.latitude, pos.coords.longitude);
    },
    onMutate: () => setLocating(true),
    onSuccess: (res) => {
      setSuggestion(nearestStoreSuggestion(res.matches, currentStoreId));
    },
    // Denied/unavailable geolocation, or a "no stores nearby" response, is
    // handled quietly — no error UI, the affordance just stays a plain button.
    onError: () => setSuggestion(null),
    onSettled: () => setLocating(false),
  });

  // Attempt once, silently, on entering the shopping status — but only when
  // the browser already has geolocation permission, so no prompt appears.
  useEffect(() => {
    if (status !== 'shopping' || autoAttempted.current) return;
    autoAttempted.current = true;
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') detect.mutate();
      })
      .catch(() => {
        // Permissions API unavailable or unsupported query — stay quiet.
      });
    // Intentionally re-run only on status change: `detect.mutate` is a stable
    // function identity from useMutation, and autoAttempted guards re-firing.
  }, [status]);

  const switchStore = useMutation({
    mutationFn: (storeId: string) => api.groceries.update(listId, { storeId }),
    onSuccess: async (list) => {
      saveLastStoreId(list.storeId);
      setSuggestion(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: groceryKeys.detail(listId) }),
        qc.invalidateQueries({ queryKey: groceryKeys.all }),
      ]);
    },
  });

  if (status !== 'open' && status !== 'shopping') return null;

  const showBanner = suggestion !== null && suggestion.id !== dismissedStoreId;

  return (
    <div className="flex flex-col gap-2">
      <Button variant="secondary" block={false} onClick={() => detect.mutate()} disabled={locating}>
        {locating ? 'Detecting…' : 'Detect store'}
      </Button>
      {showBanner ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/40 bg-accent-soft/40 px-3 py-2 text-sm"
        >
          <span className="text-ink">
            You seem to be at {suggestion.chain} — {suggestion.name} — switch list to this store?
          </span>
          <span className="flex gap-2">
            <Button
              block={false}
              onClick={() => switchStore.mutate(suggestion.id)}
              disabled={switchStore.isPending}
            >
              Switch
            </Button>
            <Button
              variant="secondary"
              block={false}
              onClick={() => setDismissedStoreId(suggestion.id)}
            >
              Dismiss
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
